import { useState, useEffect } from "react";
import { AddressMapButton } from "@/components/AddressMapButton";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plus, Building2, MapPin, Users, Pencil, Trash2, Search } from "lucide-react";
import PropertyWizard from "@/components/wizards/PropertyWizard";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Properties() {
  const [clients, setClients] = useState([]);
  const [props, setProps] = useState([]);
  const [wizard, setWizard] = useState(false);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    const [c, p] = await Promise.all([api.get("/clients"), api.get("/properties")]);
    setClients(c.data); setProps(p.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const clientName = (cid) => clients.find((c) => c.id === cid)?.name || "—";

  const doDelete = async () => {
    try { await api.delete(`/properties/${del.id}`); toast.success("Pand verwijderd"); load(); }
    catch { toast.error("Verwijderen mislukt"); }
    setDel(null);
  };

  const rows = props
    .map((p) => ({ ...p, _client: clientName(p.client_id) }))
    .filter((p) => `${p.street} ${p.number} ${p.postal_code} ${p.city} ${p._client}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => `${a.street}`.localeCompare(`${b.street}`));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#0F1E3D]">Panden &amp; Adressen</h1>
          <p className="text-slate-500">Alle adressen op een rij — met de bijbehorende opdrachtgever.</p>
        </div>
        <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => { setEdit(null); setWizard(true); }} data-testid="add-property-wizard-button">
          <Plus className="h-4 w-4 mr-2" /> Nieuw pand
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input className="pl-9" placeholder="Zoek op adres, plaats of opdrachtgever" value={q} onChange={(e) => setQ(e.target.value)} data-testid="property-search-input" />
      </div>

      {clients.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Building2 className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Maak eerst een opdrachtgever aan onder "Opdrachtgevers".</p>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="p-10 text-center border-dashed"><p className="text-slate-400">Geen panden gevonden.</p></Card>
      ) : (
        <Card className="divide-y overflow-hidden">
          {rows.map((p) => (
            <div key={p.id} onClick={() => { setEdit(p); setWizard(true); }} className="flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 cursor-pointer group" data-testid={`property-row-${p.id}`}>
              <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><MapPin className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-800 truncate">{p.street} {p.number} <AddressMapButton address={`${p.street} ${p.number}, ${p.postal_code} ${p.city}`} /></div>
                <div className="text-sm text-slate-500">{p.postal_code} {p.city}</div>
              </div>
              <div className="hidden sm:flex items-center gap-2 shrink-0">
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100"><Building2 className="h-3 w-3 mr-1" /> {p._client}</Badge>
                <Badge variant="outline">{p.type}</Badge>
                <span className="text-xs text-slate-500 flex items-center gap-1"><Users className="h-3 w-3" /> {p.tenant_count}</span>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setEdit(p); setWizard(true); }} data-testid={`property-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={(e) => { e.stopPropagation(); setDel(p); }} data-testid={`property-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </Card>
      )}

      <PropertyWizard open={wizard} onOpenChange={setWizard} initial={edit} onSaved={load} />
      <AlertDialog open={!!del} onOpenChange={() => setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Pand verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>Weet u zeker dat u dit adres wilt verwijderen?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-property">Verwijderen</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
